import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  ACTION_CLASSES, HIGHEST_EFFECTIVE_RUNG, ROUTED_ACTION_CLASSES, RUNGS, rungCovers, rungRank
} from '@marva/shared/action-classes'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { governanceApi, workspacesApi, type AuthorityConfig, type AuthorityException } from '../../services/api'
import { canAccessFeature } from '../../utils/permissions'
import { useWorkspaceRole } from '../../utils/useWorkspaceRole'
import { RUNG_COPY, RUNG_MEANING_COPY } from './rungCopy'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  FieldGrid,
  Hint,
  LoadingState,
  Section,
  SettingRow,
  Stack,
  TileGrid,
  Toggle,
} from '../../ds'

/**
 * Authority — how far Marva goes, and where she stops (D7, O1 item 6 phase 2b).
 *
 * ── What this screen is for ─────────────────────────────────────────────────────────────
 *
 * A merchant should never open Authority to CONFIGURE. They open it to SEE, and occasionally
 * to REVOKE (spec §6.5). If it starts reading as something to set up, it has become the
 * settings surface this item exists to delete.
 *
 * ── What it replaces, and the live bug that goes with it ────────────────────────────────
 *
 * The ladder here before wrote `workspace.mode` directly. That field is Marva-admin-only
 * after a 2026-07 audit, so the PUT 403'd for every ordinary merchant — and because the
 * click set local state first and swallowed the error, the screen showed "Assisted" for a
 * workspace the backend still had in shadow. Two rules follow, and both are load-bearing:
 * render the rung the SERVER returned, and let the SERVER refuse.
 *
 * ── What is deliberately absent ─────────────────────────────────────────────────────────
 *
 * The design carries two more sections: a −/value/+ stepper on each exception, and "At the
 * edge of a bound" describing the three zones. Neither ships here, because neither exists.
 * Phase 1 is refusal-only: an exception switches a class OFF, and `enforceOfferPolicy` still
 * clamps a 20% proposal to 15% exactly as it always has. Rendering "a bound escalates; it
 * never refuses" would be a claim about behaviour the platform does not have — the same
 * defect as a gate that never runs, one layer up. Both arrive with phase 1″.
 *
 * ── Where the vocabulary comes from ─────────────────────────────────────────────────────
 *
 * `ACTION_CLASSES` and `rungCovers` are imported from the same module `checkGovernance`
 * gates on, so the coverage list cannot drift from what actually refuses. A class added to
 * the model appears here with no second edit.
 *
 * ── The one switch on a coverage row (MAR-398) ──────────────────────────────────────────
 *
 * `askMerchantEnabled` used to be the Agent tab's only control. It lives on the
 * `ask_merchant` coverage row now, because that is the class it gates — but it is NOT an
 * authority exception and must never be drawn as one. It is a workspace field written through
 * `PUT /workspaces/{id}` (owner/admin, MAR-170), it gates `ask_merchant` BEFORE the authority
 * step, and it also opens the `agent_promise` slice of open-loop callbacks and the
 * `merchant_answer_received` return. So: a different chip ("Allowed, but off", never the
 * exception's "Switched off", which would contradict "Nothing is switched off" beside it), a
 * different writer, and no "Lift it". Migrating its false/absent meaning into a downward
 * exception is `authority-tabs-v6.md`'s job, not this screen's.
 *
 * ── The follow-up switch (MAR-112) ──────────────────────────────────────────────────────
 *
 * The `originate_followup` row carries a switch too, and this one IS an authority exception.
 * MAR-112 deleted the per-campaign gate (`browseFollowUp`, `campaigns.*`) and the Touchpoints
 * tab that wrote it, so the rung and its exceptions are now the whole merchant boundary on the
 * conversations she starts. Without a switch here this screen could only LIFT an exception,
 * never add one — a merchant who lifted it could not switch follow-ups off again. Off appends
 * an `off` exception; on removes every exception for the class, exactly as "Lift it" does.
 * Both go through `save` (the whole row + `expectedUpdatedAt`), and the switch renders the
 * row the server returned, never the click (D-19).
 */

type Rung = (typeof RUNGS)[number]

/** The class the follow-up switch writes exceptions for (MAR-112). */
const FOLLOWUP_CLASS = 'originate_followup'

/**
 * ⚠️ A rung above this one grants NOTHING today, and must not be offered as though it did.
 *
 * Every class anything actually requests needs `assisted` (`ROUTED_ACTION_CLASSES` — the set
 * `resolveActionClasses` can emit, guarded against drift in `governance-enforcer.test.js`). The
 * three classes `autonomous` would unlock have no caller at any rung. The ladder this screen
 * replaces marked Autonomous `disabled` / "Soon"; dropping that guard while keeping the copy
 * would have sold capability the platform does not have — a screen promising behaviour is the
 * same defect as a gate that never runs. When a routed class needs `autonomous`,
 * `HIGHEST_EFFECTIVE_RUNG` moves on its own and this unlocks with it.
 */
const rungIsEffective = (rung: Rung) => rungRank(rung) <= rungRank(HIGHEST_EFFECTIVE_RUNG)

/** What the merchant is about to change, in the words of the thing that changes. */
function movePrompt(from: Rung, to: Rung): string {
  if (to === 'shadow') {
    return 'Move her to Shadow and she stops sending. She keeps reading and drafting, and you read what she would have done.'
  }
  if (from === 'shadow') {
    return `Move her to ${RUNG_COPY[to].name} and she starts acting — inside your limits, and inside every exception below. Nothing she is not allowed to do becomes allowed.`
  }
  return `Move her to ${RUNG_COPY[to].name}. Your limits and every exception below still apply.`
}

/**
 * The server's refusal, said in the merchant's terms.
 *
 * Keyed on `details.reason` — the stable token — never on `error`, which is prose and gets
 * reworded. The old ladder pre-empted the subscription check client-side off a separate
 * `subscriptionApi` read; that is how the UI came to disagree with the backend about who was
 * allowed to go live. Entitlement, workspace state and concurrent writers are the server's
 * to know, so ask it and show what it said.
 */
function refusalMessage(reason: unknown, fallback: string): { text: string; pricing?: boolean } {
  switch (reason) {
    case 'subscription_inactive':
      return { text: 'Going live needs an active subscription.', pricing: true }
    case 'workspace_disabled':
      return { text: 'This workspace is disabled, and the rung cannot change that. Marva admins manage it.' }
    case 'account_unresolved':
      return { text: 'This workspace is not attached to an account yet, so there is nothing to bill against.' }
    case 'authority_write_conflict':
      return { text: 'Someone else changed this while you were looking, and nothing was written. This is where she stands now.' }
    case 'authority_mirror_incomplete':
      return { text: 'That saved half-way and has been left alone. Reload before changing it again.' }
    default:
      return { text: fallback }
  }
}

/**
 * Everything derived from a server response, stamped with the workspace it describes.
 *
 * ⚠️ The workspace is IN the value, not guarded beside it. Clearing state in an effect on a
 * workspace change is not enough: React renders once with the new `workspaceId` and the old
 * state before any effect runs, and that render paints workspace A's rung under workspace B's
 * name. PR #103 took three review rounds to learn this and the memory it left is *scope cached
 * state at the READ* — so the read below simply does not match, and there is no window.
 */
interface Snapshot {
  workspaceId: string
  /** `null` with `failed: true` means "we could not find out", never "assisted". */
  config: AuthorityConfig | null
  failed: boolean
  /** The server READ the row and could not parse it — a different fact from a failed request. */
  unreadable?: boolean
  /** The workspace is admin-disabled. Held on the snapshot, not on `config`, because it is true
   *  of the workspace whether or not the authority row could be read. */
  workspaceDisabled?: boolean
  /** When the row arrived, for judging exception expiry without reading the clock in render. */
  readAt: number
}

interface Feedback {
  workspaceId: string
  error?: { text: string; pricing?: boolean }
  note?: string
}

/** The ask-merchant switch's last result, stamped with the workspace it was written to. */
interface AskFeedback {
  workspaceId: string
  saved?: boolean
  failed?: boolean
}

export default function AuthorityPanel() {
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const workspaceId = currentWorkspace?.id

  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const [pending, setPending] = useState<{ workspaceId: string; rung: Rung } | null>(null)
  const [saving, setSaving] = useState<string | null>(null)
  /** Bumped by a timer when an exception is due to expire, so liveness advances with the clock. */
  const [expiryTick, setExpiryTick] = useState(0)

  // ── The ask-merchant switch (MAR-398; was the Agent tab). ──────────────────────────────
  // Escalate-and-Return. Absent = off. Read from the workspace record — it is a workspace
  // field, not part of the authority row.
  const askMerchantEnabled = (currentWorkspace as { askMerchantEnabled?: unknown } | null)?.askMerchantEnabled === true
  // The same presentation gate `RestrictedTab` applies (MAR-170): both switches' PUTs (workspace
  // and authority) refuse a member, so a member is shown the state, never a switch.
  const { role } = useWorkspaceRole()
  const canSetWorkspace = canAccessFeature(role, 'account')
  const [askSaving, setAskSaving] = useState<string | null>(null)
  const [askFeedback, setAskFeedback] = useState<AskFeedback | null>(null)
  /** Synchronous busy guard: a second click before the re-render must not issue a second PUT. */
  const askInFlight = useRef<Set<string>>(new Set())

  /**
   * Request generation PER WORKSPACE. Only the newest request for a workspace may write, and
   * only while that workspace is still the one on screen.
   *
   * ⚠️ Three windows, three guards, and each was found by removing one of the others:
   *
   * 1. The render before any effect runs → the snapshot carries its `workspaceId` and the
   *    read simply does not match (scoped at the read, the PR #103 lesson).
   * 2. A late response landing in the single snapshot slot → generations order the writers.
   *    Without this, A's late read overwrote B's good data, then failed the scope check, and
   *    left the panel on "Reading where she stands…" forever. Wrong became blank.
   * 3. ⚠️ A cross-workspace recovery reintroduced (2) by another door. A failed save for A
   *    always calls `load(A)` to recover; after a switch to B that recovery bumped a single
   *    global counter PAST B's in-flight read, so B's own response was discarded as stale and
   *    A's landed in the slot instead — B stuck on the loading line with no request left to
   *    rescue it.
   *
   * ⚠️ **What actually closes (3) is `stillCurrent`, not this keying.** The recovery load and
   * every state cleanup in `save` are gated on the workspace still being on screen, so no
   * request is ever STARTED for a workspace the user has left and the counter cannot be bumped
   * out of turn. Keying generations per workspace is insurance, and it is honest to say it is
   * currently unfalsifiable: swapping this map back for a global counter fails no test,
   * because the gates remove the starting condition. It earns its keep the day someone adds a
   * background refresh, or drops one of those gates — which is exactly how (3) arrived.
   */
  const requestSeq = useRef<Record<string, number>>({})

  /**
   * Is `asked` still the workspace on screen? Read from the store at CALL time — a value
   * captured in a closure is exactly what goes stale across a switch.
   */
  const stillCurrent = useCallback(
    (asked: string) => useWorkspaceStore.getState().currentWorkspace?.id === asked,
    []
  )

  // ── Scoped at the read. A value belonging to another workspace is simply not current. ──
  const current = snapshot?.workspaceId === workspaceId ? snapshot : null
  const config = current?.config ?? null
  const rung = (config?.effectiveRung ?? config?.rung) as Rung | undefined
  const configuredRung = (config?.configuredRung ?? (
    config?.rung === 'shadow' ? config?.savedRung ?? 'assisted' : config?.rung
  )) as Rung | undefined
  const coverageRung = rung === 'shadow' ? configuredRung : rung
  const authorityStateMismatch = config?.authorityStateMismatch === true
  const pendingRung = pending && pending.workspaceId === workspaceId ? pending.rung : null
  const shown = feedback && feedback.workspaceId === workspaceId ? feedback : null
  const error = shown?.error ?? null
  const note = shown?.note ?? null
  const busy = saving === workspaceId
  const askBusy = askSaving === workspaceId
  const askShown = askFeedback && askFeedback.workspaceId === workspaceId ? askFeedback : null

  /**
   * Push the server's derived `mode` back into the workspace store.
   *
   * ⚠️ Without this the screen is right and the rest of the app is wrong — the exact inverse
   * of the bug it deletes. `currentWorkspace.mode` is zustand-PERSISTED and refreshed only by
   * `loadWorkspaceData` on boot, so after moving the rung out of shadow the panel says "She is
   * on Assisted" while the card directly beneath it still reads "Marva is in Shadow, so the
   * chat isn't shown to shoppers", the rail badge says Shadow, and Ledger keeps its Shadow
   * banner up. Only a page reload cleared it.
   *
   * Both GET and PUT now project `mode`; applying the PUT response immediately still prevents
   * the rest of the persisted workspace store lagging behind until its next refresh.
   *
   * ⚠️ Reads the store at CALL time rather than closing over `currentWorkspace`. A save that
   * resolves after a workspace switch would otherwise spread a stale workspace object back in
   * and write the previous workspace's mode onto the new one.
   */
  const mirrorModeIntoWorkspace = useCallback((asked: string, saved: AuthorityConfig) => {
    if (!saved.mode) return
    const { currentWorkspace: live, setCurrentWorkspace } = useWorkspaceStore.getState()
    if (!live || live.id !== asked) return
    if ((live as { mode?: string }).mode === saved.mode) return
    setCurrentWorkspace({ ...live, mode: saved.mode } as never)
  }, [])

  /** Apply only if this is still the newest request for a workspace that is still on screen. */
  const applySnapshot = useCallback((asked: string, seq: number, snap: Snapshot) => {
    if (requestSeq.current[asked] !== seq) return
    // A response for a workspace the user has left must not touch the shared slot at all —
    // not even with correctly-scoped data, because it evicts whatever the current one put there.
    if (!stillCurrent(asked)) return
    setSnapshot(snap)
  }, [stillCurrent])

  const load = useCallback(async(asked: string): Promise<AuthorityConfig | null> => {
    const seq = (requestSeq.current[asked] = (requestSeq.current[asked] ?? 0) + 1)
    try {
      const res = await governanceApi.getAuthority(asked)
      // ⚠️ A row the server could not parse is a read FAILURE, not a rung. The enforcer is
      // refusing every gated class for that workspace; showing a rung beside that would be an
      // authoritative claim about a state nothing can actually read.
      if (res.success && res.data && !res.data.readError) {
        applySnapshot(asked, seq, {
          workspaceId: asked,
          config: res.data,
          failed: false,
          workspaceDisabled: res.data.workspaceDisabled,
          readAt: Date.now()
        })
        return res.data
      }
      // ⚠️ A failed read must DROP the previous config, not merely flag itself.
      //
      // Keeping it left `rung` truthy, so the honest-unknown branch never rendered and a stale
      // rung was presented as current. Worst after a write conflict: the screen said "someone
      // else changed this — this is where she stands now" while showing the pre-conflict
      // document. Never fall back to a rung, and a rung we can no longer prove is a fallback.
      applySnapshot(asked, seq, {
        workspaceId: asked,
        config: null,
        failed: true,
        unreadable: !!(res.success && res.data?.readError),
        // Still true of the workspace even when its authority row is unreadable, and it is the
        // more actionable of the two facts.
        workspaceDisabled: res.success ? res.data?.workspaceDisabled : undefined,
        readAt: Date.now()
      })
      return null
    } catch {
      applySnapshot(asked, seq, { workspaceId: asked, config: null, failed: true, readAt: Date.now() })
      return null
    }
  }, [applySnapshot])

  useEffect(() => {
    if (!workspaceId) return
    void load(workspaceId)
  }, [load, workspaceId])

  /**
   * Only the exceptions the ENFORCER still honours.
   *
   * `activeExceptionFor` in `governance-enforcer.js` requires `!expiresAt || expiresAt > now`,
   * so an expired exception has stopped narrowing anything. Listing it would show "Switched
   * off" and a *Lift it* button for a restriction that is not in force — the screen
   * contradicting behaviour `authority.test.js` already asserts of the gate.
   *
   * `readAt`/`expiryTick` keep the clock out of render: the effect below schedules a bump for
   * the next expiry, so a row that lapses while the tab is open stops being shown as live
   * instead of sitting there until a reload. `config` never has expired entries filtered OUT
   * of it, so a save round-trips the row it was given rather than quietly discarding rows the
   * merchant did not ask to drop.
   */
  const judgedAt = Math.max(current?.readAt ?? 0, expiryTick)
  const exceptions = useMemo(() => (config?.exceptions ?? []).filter(
    (x) => !x.expiresAt || new Date(x.expiresAt).getTime() > judgedAt
  ), [config, judgedAt])

  useEffect(() => {
    const next = (config?.exceptions ?? [])
      .map((x) => (x.expiresAt ? new Date(x.expiresAt).getTime() : Infinity))
      .filter((t) => t > judgedAt)
      .sort((a, b) => a - b)[0]
    if (!next || next === Infinity) return
    // `setTimeout` clamps above ~24.8 days; anything beyond that re-arms on the next render.
    const delay = Math.min(next - Date.now() + 1000, 2_000_000_000)
    if (delay <= 0) return
    const timer = setTimeout(() => setExpiryTick(Date.now()), delay)
    return () => clearTimeout(timer)
  }, [config, judgedAt])

  const save = useCallback(async(
    asked: string,
    base: AuthorityConfig,
    next: Partial<AuthorityConfig>,
    describe: (saved: AuthorityConfig) => string
  ) => {
    const seq = (requestSeq.current[asked] = (requestSeq.current[asked] ?? 0) + 1)
    setSaving(asked)
    setFeedback(null)
    try {
      const res = await governanceApi.saveAuthority(asked, {
        ...base,
        ...next,
        // ⚠️ Optimistic concurrency on the AUTHORITY row, which the server otherwise does not
        // check — its transaction conditions only on `workspace.mode`. Both a rung change and
        // a "Lift it" submit the whole document they read, so without this two same-mode
        // writers each get a 200 and the later silently discards the earlier: two tabs lifting
        // different exceptions reinstate one of them while both report success. `null` means
        // "I read no row"; the server then requires there still to be none.
        expectedUpdatedAt: base.updatedAt ?? null
      })
      if (!res.success) {
        // ⚠️ Every recovery step is gated on `asked` STILL being on screen. A failed save for
        // a workspace the user has left must not re-read it, must not clear the pending
        // selection they have since made on another one, and must not evict that workspace's
        // snapshot. Its news is about somewhere they are no longer looking.
        if (!stillCurrent(asked)) return
        // `details` is `unknown` on purpose — a 400 carries Zod's error ARRAY, not an object.
        const reason = (res.details as { reason?: unknown } | undefined)?.reason
        setFeedback({ workspaceId: asked, error: refusalMessage(reason, res.error || 'That did not save.') })
        // Re-read: after a refusal the only trustworthy rung is the one the server still has,
        // and if that read fails too the panel says so rather than showing the stale one.
        await load(asked)
        if (stillCurrent(asked)) setPending(null)
        return
      }
      if (!stillCurrent(asked)) return
      if (res.data) {
        applySnapshot(asked, seq, {
          workspaceId: asked,
          config: res.data,
          failed: false,
          workspaceDisabled: res.data.workspaceDisabled,
          readAt: Date.now()
        })
        mirrorModeIntoWorkspace(asked, res.data)
        setFeedback({ workspaceId: asked, note: describe(res.data) })
      } else {
        const reread = await load(asked)
        if (reread) {
          mirrorModeIntoWorkspace(asked, reread)
          setFeedback({ workspaceId: asked, note: describe(reread) })
        }
      }
      if (stillCurrent(asked)) setPending(null)
    } catch (e) {
      if (!stillCurrent(asked)) return
      setFeedback({
        workspaceId: asked,
        error: { text: e instanceof Error ? e.message : 'That did not save.' }
      })
      await load(asked)
      if (stillCurrent(asked)) setPending(null)
    } finally {
      setSaving((s) => (s === asked ? null : s))
    }
  }, [load, applySnapshot, mirrorModeIntoWorkspace, stillCurrent])

  const confirmRung = () => {
    if (!pendingRung || !config || !workspaceId) return
    // The note names the rung the SERVER came back with, not the one that was clicked — the
    // one place left where the screen could narrate from the click.
    void save(workspaceId, config, { rung: pendingRung }, (saved) => (
      (saved.effectiveRung ?? saved.rung) === 'shadow'
        ? 'She is in Shadow. Nothing leaves until you move her back.'
        : `She is on ${RUNG_COPY[(saved.effectiveRung ?? saved.rung) as Rung]?.name ?? saved.rung}.`
    ))
  }

  const lift = (target: AuthorityException) => {
    if (!config || !workspaceId) return
    const label = ACTION_CLASSES[target.actionClass]?.label ?? target.actionClass
    // Removes from the row as READ, not from the filtered view — an expired exception the
    // screen hides must not be silently dropped from the document on an unrelated save.
    void save(
      workspaceId,
      config,
      { exceptions: (config.exceptions ?? []).filter((x) => x.actionClass !== target.actionClass) },
      () => `Lifted. She may ${label.toLowerCase()} again.`
    )
  }

  /**
   * The follow-up switch (MAR-112): add or remove the `originate_followup` exception.
   *
   * `on` is what the merchant asked for, computed from the STORED state the switch shows. Off
   * appends a fresh `off` exception — dropping any expired one for the class first, since it
   * narrows nothing and two rows for one class would be ambiguous. On removes every exception
   * for the class, the same document "Lift it" writes. Both remove from the row as READ, so an
   * expired exception of another class survives an unrelated save.
   *
   * ⚠️ Synchronous in-flight guard: two clicks before the busy render would otherwise send two
   * PUTs pinned to the same `expectedUpdatedAt`, and the second would come back as a conflict.
   */
  const followupInFlight = useRef<Set<string>>(new Set())
  const setFollowups = (on: boolean) => {
    if (!config || !workspaceId) return
    if (followupInFlight.current.has(workspaceId)) return
    followupInFlight.current.add(workspaceId)
    const asked = workspaceId
    const others = (config.exceptions ?? []).filter((x) => x.actionClass !== FOLLOWUP_CLASS)
    const exceptionsNext: AuthorityException[] = on
      ? others
      : [
          ...others,
          {
            actionClass: FOLLOWUP_CLASS,
            kind: 'off',
            grantedVia: 'ui',
            expiresAt: null,
            createdAt: new Date().toISOString(),
            note: 'By you, with the switch above'
          }
        ]
    void save(
      asked,
      config,
      { exceptions: exceptionsNext },
      () => (on
        ? 'Switched on. She may start conversations you did not ask for again.'
        : 'Switched off. She starts no conversations you did not ask for.')
    ).finally(() => { followupInFlight.current.delete(asked) })
  }

  /**
   * Flip `askMerchantEnabled` through the workspace PUT — the same write the Agent tab made.
   *
   * D-19: the switch reports the write. It is busy while the PUT is in flight and moves only
   * when the server said yes, so a refusal or a network failure leaves it where it was (the
   * rollback is that nothing moved) and says so. The workspace is read from the store at CALL
   * time and the result is applied only while that workspace is still on screen, so a write
   * resolving after a switch never lands the old workspace's value on the new one.
   */
  const toggleAsk = useCallback(async() => {
    const live = useWorkspaceStore.getState().currentWorkspace
    if (!live) return
    const asked = live.id
    if (askInFlight.current.has(asked)) return
    askInFlight.current.add(asked)
    const next = !((live as { askMerchantEnabled?: unknown }).askMerchantEnabled === true)
    setAskSaving(asked)
    setAskFeedback(null)
    try {
      const res = await workspacesApi.update(asked, { askMerchantEnabled: next } as never)
      if (!res?.success) throw new Error('not saved')
      const now = useWorkspaceStore.getState()
      if (now.currentWorkspace?.id !== asked) return
      now.setCurrentWorkspace({ ...now.currentWorkspace, askMerchantEnabled: next } as never)
      setAskFeedback({ workspaceId: asked, saved: true })
    } catch {
      if (stillCurrent(asked)) setAskFeedback({ workspaceId: asked, failed: true })
    } finally {
      askInFlight.current.delete(asked)
      setAskSaving((s) => (s === asked ? null : s))
    }
  }, [stillCurrent])

  // "Saved" is a moment, not a state.
  useEffect(() => {
    if (!askFeedback?.saved) return
    const timer = setTimeout(() => setAskFeedback((f) => (f === askFeedback ? null : f)), 2000)
    return () => clearTimeout(timer)
  }, [askFeedback])

  /**
   * The ask-merchant row: the switch for an owner/admin, the state for everyone else.
   *
   * ⚠️ The switch is the DS row-form Toggle, labelled by the row's visible title and described
   * by its visible description (WCAG 2.5.3 — the accessible name contains what is on screen).
   * A failure is said directly under it, not elsewhere in the card.
   */
  const askTitle = ACTION_CLASSES.ask_merchant.label
  const askDescription = (
    <>
      When a customer asks something she genuinely can&apos;t answer from your catalogue or store details,
      she can send you the question instead of guessing — then go back to the customer with your answer.
    </>
  )
  const renderAskRow = (chip: ReactNode) => (
    <>
      {canSetWorkspace ? (
        <SettingRow
          aside={<>{chip}{askShown?.saved && <Badge tone="sage">Saved</Badge>}</>}
        >
          {/* D-19: the switch reports the write — busy while it is in flight, never optimistic. */}
          <Toggle
            checked={askMerchantEnabled}
            onChange={() => void toggleAsk()}
            busy={askBusy}
            label={askTitle}
            description={askDescription}
          />
        </SettingRow>
      ) : (
        <SettingRow title={askTitle} description={askDescription} aside={chip} />
      )}
      {askShown?.failed && (
        <Alert tone="danger" live="assertive">
          That did not save. Bringing you questions is still {askMerchantEnabled ? 'on' : 'off'}.
        </Alert>
      )}
    </>
  )
  /**
   * The follow-up row: the switch for an owner/admin, the state for everyone else — the same
   * presentation gate as the ask row (`PUT /governance/authority` is owner/admin, MAR-175).
   *
   * Checked = the rung covers the class AND no live exception switches it off — the stored row,
   * never the click. Disabled when the rung does not cover the class (switching it on could not
   * make her do it); busy while any authority save is in flight. A refusal is said in the
   * panel's one error alert, and the switch stays where the re-read row puts it.
   */
  const followupDescription = (
    <>
      Following up after someone browses, a restock alert, a price-drop alert. Off, she starts none of
      them; she still answers anyone who writes to her.
    </>
  )
  const renderFollowupRow = (title: string, covered: boolean, off: boolean, chip: ReactNode) => (
    canSetWorkspace ? (
      <SettingRow aside={chip}>
        <Toggle
          checked={covered && !off}
          onChange={() => setFollowups(!(covered && !off))}
          busy={busy}
          disabled={!covered}
          label={title}
          description={followupDescription}
        />
      </SettingRow>
    ) : (
      <SettingRow title={title} description={followupDescription} aside={chip} />
    )
  )

  /** What the switch does — carried over from the Agent tab, corrected where it had gone stale. */
  const renderAskExplainer = (shadow: boolean, held?: string) => (
    <CardBody>
      <Stack gap="sm">
        {/* A switch that is on is not the same as questions reaching you. Said first, so the
            "When enabled" paragraph below is never read as "this is working". */}
        {held && askMerchantEnabled && <Alert tone="attention">{held}</Alert>}
        <Card tone="recessed" padding="sm">
          <CardBody>
            <Stack gap="sm">
              <p>
                <b>When enabled:</b> she tells the customer honestly that she&apos;s checking with you, and emails
                you the question. Reply to that email, or answer from the queue under Notifications. Your answer
                goes back to the customer in your words — she never embellishes it — and is saved so the same
                question never reaches you twice. If the customer hasn&apos;t left an email address, she offers to
                take one first and promises nothing until they do; the question reaches you once they share it.
                At most one open question per customer, and five a day across the store. If nobody answers
                within 48 hours the customer gets an honest &ldquo;we couldn&apos;t get a firm answer&rdquo;
                rather than silence — turning this on is also what lets her come back to a customer she
                promised to get back to.
              </p>
              <p>
                <b>When disabled:</b> she never offers to check with you. When she doesn&apos;t know something she
                says so plainly and points the customer at your contact details. Nothing reaches your inbox.
              </p>
            </Stack>
          </CardBody>
        </Card>
        <Hint>Changes take up to 5 minutes to reach conversations that are already open.</Hint>
        <Hint>
          Questions are emailed to the addresses under{' '}
          <Link to="/account?tab=notifications">Account → Notifications</Link>. With none set they&apos;ll only appear in your
          dashboard queue — nothing will land in your inbox.
        </Hint>
        {shadow && (
          <Alert tone="info">
            Marva is in <b>Shadow</b>, so the chat isn&apos;t shown to shoppers and no questions will reach you
            yet. You can set this now — it takes effect when you move her to Assisted.
          </Alert>
        )}
      </Stack>
    </CardBody>
  )
  /**
   * ⚠️ The switch on its own, for every state with no rung to show (read failed, row unreadable,
   * nothing set, workspace disabled). It is a workspace field, not part of the authority row, so
   * an authority read that failed must not take away the merchant's way to turn it OFF — the
   * Agent tab rendered it whatever governance said. "On" / "Off" only: with no rung read, the
   * screen cannot say what the rung allows.
   */
  const renderAskCard = (held?: string) => (
    <Card>
      <CardHeader
        title="Ask you when she doesn't know"
        description="What happens when a customer asks something only you can answer."
      />
      <div>
        {/* Never sage while something holds every question: a green "On" reads as "working". */}
        {renderAskRow(<Chip tone={askMerchantEnabled && !held ? 'sage' : 'neutral'}>{askMerchantEnabled ? 'On' : 'Off'}</Chip>)}
      </div>
      {renderAskExplainer(false, held)}
    </Card>
  )

  /**
   * What the RUNG governs — computed from the shared model, never listed here.
   *
   * ⚠️ Two corrections over the obvious version, and both were wrong answers on screen first:
   *
   * 1. **Routed, not merely covered.** `rungCovers()` answers "would this rung permit it",
   *    which is not "will anything ever ask". Mapping over every declared class told a merchant
   *    on `autonomous` that Marva "starts returns" — no caller requests that class at any rung.
   *    `ROUTED_ACTION_CLASSES` is the set `resolveActionClasses` can actually emit.
   * 2. **"The rung allows this", not "she does this."** The rung says what she MAY do; whether
   *    she does it for a given customer is her judgment from the evidence, and choosing not to
   *    act is a decision too. `askMerchantEnabled` also still gates `ask_merchant` before the
   *    authority step (its own chip says so). The per-campaign gate that once sat in front of
   *    the outbound follow-ups is gone (MAR-112), so for `originate_followup` the rung and its
   *    exceptions are the whole boundary — and that row carries their switch.
   */
  const coverage = useMemo(() => {
    if (!coverageRung) return []
    return ROUTED_ACTION_CLASSES.map((id) => {
      const cls = ACTION_CLASSES[id]
      const off = exceptions.some((x) => x.actionClass === id)
      const covered = rungCovers(coverageRung, id)
      // ⚠️ The workspace switch, not an exception: the rung allows it, the switch holds it.
      // Checked after the exception (the narrower authority fact wins) and after the rung (a
      // class the rung does not cover needs the rung first, whatever the switch says).
      const switchedOffHere = id === 'ask_merchant' && covered && !off && !askMerchantEnabled
      return {
        id,
        label: cls.label,
        covered,
        off,
        state: off
          ? 'Switched off'
          : !covered
            ? `Needs ${RUNG_COPY[cls.needs].name}`
            : switchedOffHere ? 'Allowed, but off' : 'The rung allows this',
        tone: (!off && covered && !switchedOffHere ? 'sage' : 'neutral') as 'neutral' | 'sage'
      }
    })
  }, [coverageRung, exceptions, askMerchantEnabled])

  if (!workspaceId) return null

  if (!current) {
    return (
      <Card>
        <LoadingState caption="Reading where she stands…" />
      </Card>
    )
  }

  if (current.workspaceDisabled) {
    // ⚠️ A distinct state, never a rung. `disabled` is admin-only and the rung cannot express
    // it. Since MAR-24 every send path holds it (`@marva/shared/send-hold`), so "nothing goes
    // out" is now true — but it is NOT Shadow: not a preview, and its held decisions are
    // recorded `suppressed`, not as drafts to review.
    return (
      <Stack>
        <Alert tone="attention">
          This workspace is disabled, so Marva sends nothing to your shoppers and there is no
          authority to set on it. Marva admins manage that state — talk to us and we will sort
          it out.
        </Alert>
        {renderAskCard('Set, but nothing reaches you while this workspace is disabled.')}
      </Stack>
    )
  }

  if (!rung) {
    // Honest unknown. The alternative — showing a default, or the rung from the last read that
    // worked — is the failure this phase exists to remove.
    return (
      <Stack>
        <Alert tone={current.failed ? 'danger' : 'info'}>
          {current.unreadable
            // Says what she is DOING meanwhile, because "we cannot read it" on its own leaves
            // the merchant to guess, and the guess that matters is whether she is still acting.
            ? 'Her authority setting could not be read, so nothing is shown rather than a guess. She is refusing anything that needs your permission until it is fixed — tell us and we will repair it.'
            : current.failed
              ? 'Could not read where she stands, so nothing is shown rather than a guess. Reload to try again.'
              : 'Nothing is set for this workspace yet.'}
        </Alert>
        {/* Unreadable: the enforcer refuses every gated class, `ask_merchant` included. A failed
            REQUEST says nothing about the row, so it gets no such claim. */}
        {renderAskCard(current.unreadable ? 'Set, but nothing reaches you while her authority setting can’t be read.' : undefined)}
      </Stack>
    )
  }

  return (
    <Section
      title="How far she goes, and where she stops"
      description={
        <>
          You set the boundary; she does the work. This is what she <em>may</em> do — not how she does
          it. You can drop her a rung at any time, and it takes effect on her next message.
        </>
      }
    >
      <Stack>
        {authorityStateMismatch && (
          <Alert tone="danger" live="assertive">
            Her saved authority and workspace mode disagree, so nothing that needs authority is
            allowed. Tell us and we will repair this state before she acts.
          </Alert>
        )}
        {rung === 'shadow' && configuredRung && !authorityStateMismatch && (
          <Alert tone="info">
            Marva is in <b>Shadow</b>. She keeps reading and drafting, but ordinary customer
            communications are held. When activated, she returns to{' '}
            <b>{RUNG_COPY[configuredRung].name}</b> with the limits and exceptions shown below.
            Carrier-required SMS replies such as STOP and HELP are the exception.
          </Alert>
        )}
        {/* ── The rung ───────────────────────────────────────────────────────────────
            Three across, two per row below a 900px container and one below 520px (TileGrid columns={3}). The
            rung she is on says so in words inside its own card, rather than with a badge. */}
        <TileGrid columns={3} role="group" aria-label="The rung">
          {RUNGS.map((r) => {
            const on = rung === r
            const saved = rung === 'shadow' && configuredRung === r
            const effective = rungIsEffective(r)
            return (
              <Card
                key={r}
                interactive
                selectable
                selected={on}
                disabled={busy || (!effective && !on && !saved)}
                title={effective || saved ? undefined : 'Nothing routes to these actions yet'}
                onClick={() => {
                  setFeedback(null)
                  setPending(on || !workspaceId ? null : { workspaceId, rung: r })
                }}
              >
                <CardHeader
                  inline
                  title={RUNG_COPY[r].name}
                  description={on && authorityStateMismatch
                    ? 'Saved authority conflicts with workspace mode. Operator repair is required.'
                    : RUNG_COPY[r].desc}
                  aside={
                    <Badge tone={pendingRung === r ? 'gold' : on ? 'sage' : 'neutral'}>
                      {on && authorityStateMismatch
                        ? 'Needs repair'
                        : saved ? 'Saved' : effective ? RUNG_COPY[r].tag : 'Not yet'}
                    </Badge>
                  }
                >
                  {on && !authorityStateMismatch && <Hint as="span">She is on {RUNG_COPY[r].name}.</Hint>}
                  {on && authorityStateMismatch && <Hint as="span">Workspace and authority state disagree.</Hint>}
                  {saved && <Hint as="span">She returns here when activated.</Hint>}
                </CardHeader>
              </Card>
            )
          })}
        </TileGrid>

        {/* Going live is the highest-consequence act in the product; it used to be one click
            with a tooltip as its only warning (UX audit §4.2). */}
        {pendingRung && pendingRung !== rung && (
          <Alert
            tone="attention"
            actions={
              <>
                <Button size="sm" busy={busy} busyLabel="Moving her…" onClick={confirmRung}>
                  Move her
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => { setPending(null); setFeedback(null) }}
                >
                  Cancel
                </Button>
              </>
            }
          >
            {movePrompt(rung, pendingRung)}
          </Alert>
        )}

        {error && (
          <Alert tone="danger" live="assertive">
            {error.text}
            {error.pricing && (
              <>
                {' '}
                <Link to="/pricing">See plans</Link>
              </>
            )}
          </Alert>
        )}
        {note && !error && <Alert tone="success">{note}</Alert>}

        {/* D5: authority and confidence veto independently — raising the rung does not make
            her more capable, and the dial invites exactly that assumption. */}
        <p className="t-small">
          The rung says whether she <b>may</b>. What she knows says whether she <b>should</b>. Either one
          stops her, and every decision is recorded either way — so moving her up or down is never a
          leap of faith.
        </p>

        <FieldGrid>
          {/* ── Coverage ─────────────────────────────────────────────────────────────
              Always shown. It used to sit behind "Show the detail", which hid the one list
              that says what the rung on screen actually permits. */}
          <Card>
            <CardHeader
              title={rung === 'shadow' && coverageRung
                ? `What ${RUNG_COPY[coverageRung].name} will mean`
                : `What ${RUNG_COPY[rung].name} means`}
              description={authorityStateMismatch
                ? 'This is the saved boundary. The workspace state must be repaired before it is safe to describe what is active.'
                : rung === 'shadow'
                  ? 'This is the saved live boundary. Shadow itself holds ordinary customer communications.'
                : RUNG_MEANING_COPY.acting}
            />
            <div>
              {coverage.map((c) => (
                c.id === 'ask_merchant' ? (
                  <div key={c.id}>{renderAskRow(<Chip tone={c.tone}>{c.state}</Chip>)}</div>
                ) : c.id === FOLLOWUP_CLASS ? (
                  <div key={c.id}>{renderFollowupRow(c.label, c.covered, c.off, <Chip tone={c.tone}>{c.state}</Chip>)}</div>
                ) : (
                  <SettingRow
                    key={c.id}
                    title={c.label}
                    aside={<Chip tone={c.tone}>{c.state}</Chip>}
                  />
                )
              ))}
            </div>
            {renderAskExplainer(rung === 'shadow' && !authorityStateMismatch)}
            {/* ⚠️ Chat is ungated by authority by design, so answering and recommending belong
                to no rung at all — said plainly instead of implied. The conversations she starts
                are the opposite: since MAR-112 nothing else gates them. */}
            <CardFooter>
              Answering a shopper and recommending a product are replies to something she was just
              asked, so no rung governs them — she does those whenever the chat is on. The
              conversations she starts follow the rung and the exceptions here.
            </CardFooter>
          </Card>

          {/* ── Exceptions ───────────────────────────────────────────────────────────── */}
          {/* Sized to its content: stretched to the coverage card's height it was a tall empty box. */}
          <Card fit>
            <CardHeader
              title={
                <>
                  Except… {exceptions.length > 0 && <Badge count={exceptions.length} />}
                </>
              }
              aside={<Hint as="span">Only ever narrower, never wider</Hint>}
            />
            {exceptions.length === 0 ? (
              <CardBody>
                {/* ⚠️ The ask-merchant switch is not an exception, but "she does everything the rung
                    covers" is false while it is off — so this says so, without calling it one. */}
                {coverageRung && rungCovers(coverageRung, 'ask_merchant') && !askMerchantEnabled ? (
                  <>
                    Nothing is switched off here. She does everything {RUNG_COPY[coverageRung].name} covers except
                    bring you questions — asking you is off — and nothing above it.
                  </>
                ) : (
                  <>
                    Nothing is switched off. She does everything {RUNG_COPY[coverageRung ?? rung].name} covers, and nothing
                    above it.
                  </>
                )}
              </CardBody>
            ) : (
              <div>
                {exceptions.map((x) => (
                  <SettingRow
                    key={x.actionClass}
                    title={ACTION_CLASSES[x.actionClass]?.label ?? x.actionClass}
                    description={
                      <>
                        Switched off
                        {/*
                          ⚠️ Keyed on the ROW's `migratedAt`, not on `grantedVia`. Both the one-time
                          cutover and the go-live choke point write `grantedVia: 'migration'` — the
                          enum cannot take a third value until every reader is redeployed (MAR-231),
                          because the enforcer refuses every gated class on a row it cannot parse. Only
                          a cutover row carries `migratedAt`, so that is what this sentence is true of.
                          Keyed on `grantedVia` alone it told a merchant whose workspace went live last
                          week that their setting was "carried over", and contradicted the note beside
                          it. Keyed on `migratedAt` alone it said the same of the merchant's OWN
                          exceptions on a cutover row. It takes both: this exception came from a
                          migration, AND this row is the one the migration wrote.
                        */}
                        {x.grantedVia === 'migration' && config?.migratedAt &&
                          ' — carried over from your old touchpoint settings'}
                        {x.note ? ` · ${x.note}` : ''}
                      </>
                    }
                    aside={
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => lift(x)}>
                        Lift it
                      </Button>
                    }
                  />
                ))}
              </div>
            )}
            {/* The ceilings are Limits' to set; this screen only ever narrows. `?tab=` is the
                Authority screen's contract for landing on a named tab. */}
            <CardFooter>
              <span>
                The ceilings on discounts and messages live in{' '}
                <Link to="/authority?tab=limits">Limits</Link>. Exceptions here only ever narrow what the rung allows.
              </span>
            </CardFooter>
          </Card>
        </FieldGrid>
      </Stack>
    </Section>
  )
}
